import React, { useEffect } from 'react';
import { Box, useInView } from '@stockx/ui';
import { SmartGridRow } from '@app/ui';
import { usePageType } from '@app/page';
import { PageType } from '@app/config';
import type { LegacyProduct } from '@app/types';
import { trackEvent as segmentTrackEvent, EVENTS } from '@app/segment';
import DynamicProductRowItem from './DynamicProductRowItem';
import DynamicRowHeader from './DynamicRowHeader';

type DynamicProductRowProps = {
  screenName: string;
  swooshName: string;
  header: string;
  tileType: string;
  priceType: string;
  footnotesType: string;
  seeAllCTA?: any;
  seeAccountCTA?: any;
  trackingEvent: string;
  helpMessage?: string;
  helpMessageTracking?: Function;
  products?: LegacyProduct[];
};

const DynamicProductRow = ({
  screenName,
  swooshName,
  header,
  tileType,
  priceType,
  footnotesType,
  seeAllCTA,
  seeAccountCTA,
  trackingEvent,
  helpMessage = '',
  helpMessageTracking,
  products,
}: DynamicProductRowProps) => {
  const isCalendar = tileType === 'CALENDAR_TILE';
  const browsePageSelector =
    priceType === 'HIGHEST_BID' ? 'highestBid' : 'lowestAsk';

  const pageType = usePageType();

  const [ref, inView] = useInView({
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      segmentTrackEvent(EVENTS.PRODUCT_CAROUSEL_VIEWED, {
        swooshName: trackingEvent,
        screenName: pageType,
      });
    }
  }, [inView]);

  return (
    <Box data-component="ProductRow" ref={ref}>
      <DynamicRowHeader
        header={header}
        seeAllCTA={seeAllCTA}
        seeAccountCTA={seeAccountCTA}
        helpMessage={helpMessage}
        helpMessageTracking={helpMessageTracking}
        rel={isCalendar ? 'nofollow' : ''}
      />
      <SmartGridRow
        isHomepage={pageType === PageType.Home}
        columns={6}
        isFullBleed
      >
        {products?.map((product: LegacyProduct, index: number) => (
          <DynamicProductRowItem
            key={product.uuid}
            isCalendar={isCalendar}
            product={product}
            browsePageSelector={browsePageSelector}
            index={index}
            trackingEvent={trackingEvent}
            screenName={screenName}
            swooshName={swooshName}
            footnotesType={footnotesType}
          />
        ))}
      </SmartGridRow>
    </Box>
  );
};

export default DynamicProductRow;
